import { Label, LabelContainer, Placeholder } from './style';
import { Tooltip } from '@mantine/core';
import { REPORT_TYPES } from '../../../consts/reportTypes';

export const statusKeys = {
  AIF: 'aif',
  FILES: 'files',
  JURISDICTION: 'jurisdiction',
  EXTRACTION: 'extraction',
  EXTRACTION_IN_PROGRESS: 'extraction_in_progress',
  TRANSFORMATION: 'transformation',
  TRANSFORMATION_IN_PROGRESS: 'transformation_in_progress',
  REPORT: 'report',
  APPROVAL: 'approval',
  SUBMISSION: 'submission',
  END: 'end'
};

export const APPROVAL_STATUSES = {
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  NOT_STARTED: 'Not started'
};

export const statusLabels = {
  [statusKeys.AIF]: 'Add Funds',
  [statusKeys.FILES]: 'Upload Files',
  [statusKeys.JURISDICTION]: 'Add Jurisdictions',
  [statusKeys.EXTRACTION]: 'Create Extraction',
  [statusKeys.EXTRACTION_IN_PROGRESS]: 'Extraction in progress',
  [statusKeys.TRANSFORMATION]: 'Create Transformation',
  [statusKeys.TRANSFORMATION_IN_PROGRESS]: 'Transformation in progress',
  [statusKeys.REPORT]: 'Generate Report',
  [statusKeys.APPROVAL]: 'Approval Pending',
  [statusKeys.SUBMISSION]: 'Submit'
};

export const isFromScratch = period => period?.template_type === 'sratch';

export const isMinimalFiling = period =>
  period?.filing_type === 'PF_TRANS' || period?.filing_type === 'PF_FINAL';

// True when every included fund's national code in the period is Nil Return —
// backend-computed flag. Such periods skip the data stages (Source Files/Extraction/
// Transformation/Lookup Tables/Output Data).
export const isNilReturnPeriod = period => period?.all_national_codes_nil_return === true;

export const getStatusForConversionReportType = period => {
  // For conversion periods the backend doesn't reliably flip
  // *_progress_status past "Not started" for setup steps (files / funds /
  // jurisdictions) — the presence of at least one row is authoritative.
  // Advance the cursor as soon as a count exists so each stage turns green
  // when it's actually done instead of all-at-once after report generation.
  if (!period.file_count) return statusKeys.FILES;
  if (!period.fund_count) return statusKeys.AIF;
  if (!period.jurisdiction_count) return statusKeys.JURISDICTION;

  // Report logic - start
  if (
    period.report_progress_status === 'Not started' ||
    (period?.report_task && period?.report_task?.status !== 'Completed')
  )
    return statusKeys.REPORT;

  if (period?.comments?.total_threads !== period?.comments?.resolved_threads)
    return statusKeys.REPORT;
  // Report logic - end

  // Stop advancing past APPROVAL / SUBMISSION for ANY non-Completed value
  // (was `=== 'Not started'` — which let 'In progress' fall through to END
  // and painted every stepper stage green while approval was still running).
  // Truthiness guard keeps pre-approval periods (undefined field) at END.
  if (period.approve_progress_status && period.approve_progress_status !== 'Completed')
    return statusKeys.APPROVAL;

  if (period.submit_progress_status && period.submit_progress_status !== 'Completed')
    return statusKeys.SUBMISSION;

  return statusKeys.END;
};

export const getPlaceholderForConversionReportType = period => {
  // Matches getStatusForConversionReportType — count-only gates so the
  // sub-label advances in step with the stage cursor.
  if (!period.file_count) return statusLabels[statusKeys.FILES];
  if (!period.fund_count) return statusLabels[statusKeys.AIF];
  if (!period.jurisdiction_count) return statusLabels[statusKeys.JURISDICTION];

  if (
    period.report_progress_status === 'Not started' ||
    (period?.report_task && period?.report_task?.status !== 'Completed')
  )
    return statusLabels[statusKeys.REPORT];

  if (period?.comments?.total_threads !== period?.comments?.resolved_threads)
    return 'Resolve comments first';

  if (period.approve_progress_status && period.approve_progress_status !== 'Completed')
    return statusLabels[statusKeys.APPROVAL];
  // if (period.report_progress_status === 'Not started')
  if (period.submit_progress_status && period.submit_progress_status !== 'Completed')
    return statusLabels[statusKeys.SUBMISSION];
  return null;
};

export const getStatusForPFTrans = period => {
  if (
    period.report_progress_status === 'Not started' ||
    (period?.report_task && period?.report_task?.status !== 'Completed')
  )
    return statusKeys.REPORT;

  if (period?.comments?.total_threads !== period?.comments?.resolved_threads)
    return statusKeys.REPORT;

  // Stop advancing past APPROVAL / SUBMISSION for ANY non-Completed value
  // (was `=== 'Not started'` — which let 'In progress' fall through to END
  // and painted every stepper stage green while approval was still running).
  // Truthiness guard keeps pre-approval periods (undefined field) at END.
  if (period.approve_progress_status && period.approve_progress_status !== 'Completed')
    return statusKeys.APPROVAL;

  if (period.submit_progress_status && period.submit_progress_status !== 'Completed')
    return statusKeys.SUBMISSION;

  return statusKeys.END;
};

// Derives a placeholder label from a pre-computed status key.
// For REPORT, distinguishes between "report not done" and "resolve comments first".
const _placeholderFromStep = (period, step) => {
  if (step === statusKeys.REPORT) {
    const reportDone =
      period.report_progress_status !== 'Not started' &&
      (!period?.report_task || period?.report_task?.status === 'Completed');
    if (reportDone && period?.comments?.total_threads !== period?.comments?.resolved_threads)
      return 'Resolve comments first';
  }
  return statusLabels[step];
};

export const getPlaceholderForPFTrans = period =>
  _placeholderFromStep(period, getStatusForPFTrans(period));

export const getStatusForPFFinal = period => {
  if (
    period.report_progress_status === 'Not started' ||
    (period?.report_task && period?.report_task?.status !== 'Completed')
  )
    return statusKeys.REPORT;

  if (period?.comments?.total_threads !== period?.comments?.resolved_threads)
    return statusKeys.REPORT;

  // Stop advancing past APPROVAL / SUBMISSION for ANY non-Completed value
  // (was `=== 'Not started'` — which let 'In progress' fall through to END
  // and painted every stepper stage green while approval was still running).
  // Truthiness guard keeps pre-approval periods (undefined field) at END.
  if (period.approve_progress_status && period.approve_progress_status !== 'Completed')
    return statusKeys.APPROVAL;

  if (period.submit_progress_status && period.submit_progress_status !== 'Completed')
    return statusKeys.SUBMISSION;

  return statusKeys.END;
};

export const getPlaceholderForPFFinal = period =>
  _placeholderFromStep(period, getStatusForPFFinal(period));

export const getStatusForCustomReportType = period => {
  if (period.file_mapping_progress_status === 'Not started' || !period.file_count)
    return statusKeys.FILES;

  // Extraction logic -- start
  if (period.extraction_progress_status === 'Not started') return statusKeys.EXTRACTION;
  if (period.extraction_stats?.failed_extractions_number > 0) return statusKeys.EXTRACTION;
  if (period?.extraction_stats?.in_progress_extractions_number > 0)
    return statusKeys.EXTRACTION_IN_PROGRESS;
  if (period?.extraction_stats?.successful_extractions_number === 0)
    return statusKeys.EXTRACTION_IN_PROGRESS;
  // Extraction logic -- end

  // Transformation logic -- start
  const allTransformations =
    period?.transformation_stats?.successful_transformations_number +
    period?.transformation_stats?.failed_transformations_number +
    period?.transformation_stats?.in_progress_transformation_number;

  const isTransformationStartedOrCompleted =
    period.transformation_progress_status !== 'Not started';

  const skipTransformationStatusCheck =
    allTransformations === 0 && isTransformationStartedOrCompleted;

  if (period.transformation_progress_status === 'Not started' && !skipTransformationStatusCheck)
    return statusKeys.TRANSFORMATION;
  if (
    period.transformation_stats?.failed_transformations_number > 0 &&
    !skipTransformationStatusCheck
  )
    return statusKeys.TRANSFORMATION;
  if (
    // period?.transformation_stats?.successful_transformations_number === 0 ||
    period?.transformation_stats?.in_progress_transformation_number > 0 &&
    !skipTransformationStatusCheck
  )
    return statusKeys.TRANSFORMATION;
  // Transformation logic -- end

  // Report logic - start
  // if (
  //   period.report_progress_status === 'Not started' ||
  //   (period?.report_task && period?.report_task?.status !== 'Completed')
  // )
  //   return statusKeys.REPORT;

  // if (period?.comments?.total_threads !== period?.comments?.resolved_threads)
  //   return statusKeys.REPORT;
  // Report logic - end

  // Stop advancing past APPROVAL / SUBMISSION for ANY non-Completed value
  // (was `=== 'Not started'` — which let 'In progress' fall through to END
  // and painted every stepper stage green while approval was still running).
  // Truthiness guard keeps pre-approval periods (undefined field) at END.
  if (period.approve_progress_status && period.approve_progress_status !== 'Completed')
    return statusKeys.APPROVAL;

  if (period.submit_progress_status && period.submit_progress_status !== 'Completed')
    return statusKeys.SUBMISSION;

  return statusKeys.END;
};

export const getPlaceholderForCustomReportType = period => {
  if (period.file_mapping_progress_status === 'Not started' && !period.file_count)
    return statusLabels[statusKeys.FILES];

  // Extraction logic -- start
  if (period.extraction_progress_status === 'Not started')
    return statusLabels[statusKeys.EXTRACTION];
  if (period.extraction_stats?.failed_extractions_number > 0) return 'Resolve issues first';
  if (period?.extraction_stats?.in_progress_extractions_number > 0)
    return statusLabels[statusKeys.EXTRACTION_IN_PROGRESS];
  if (period?.extraction_stats?.successful_extractions_number === 0)
    return statusLabels[statusKeys.EXTRACTION_IN_PROGRESS];
  // Extraction logic -- end

  // Transformation logic -- start
  const allTransformations =
    period?.transformation_stats?.successful_transformations_number +
    period?.transformation_stats?.failed_transformations_number +
    period?.transformation_stats?.in_progress_transformation_number;

  const isTransformationStartedOrCompleted =
    period.transformation_progress_status !== 'Not started';

  const skipTransformationStatusCheck =
    allTransformations === 0 && isTransformationStartedOrCompleted;

  if (
    period.transformation_stats?.failed_transformations_number > 0 &&
    !skipTransformationStatusCheck
  )
    return 'Resolve issues first';
  if (
    (!period.transformation_progress_status ||
      period.transformation_progress_status === 'Not started') &&
    !skipTransformationStatusCheck
  )
    return statusLabels[statusKeys.TRANSFORMATION];
  // if (period?.transformation_stats?.successful_transformations_number === 0)
  //   return statusLabels[statusKeys.TRANSFORMATION];
  if (
    period?.transformation_stats?.in_progress_transformation_number > 0 &&
    !skipTransformationStatusCheck
  )
    return statusLabels[statusKeys.TRANSFORMATION_IN_PROGRESS];
  // Transformation logic -- end

  // if (
  //   period.report_progress_status === 'Not started' ||
  //   (period?.report_task && period?.report_task?.status !== 'Completed')
  // )
  //   return statusLabels[statusKeys.REPORT];

  if (period?.comments?.total_threads !== period?.comments?.resolved_threads)
    return 'Resolve comments first';

  if (period.approve_progress_status && period.approve_progress_status !== 'Completed')
    return statusLabels[statusKeys.APPROVAL];
  // if (period.report_progress_status === 'Not started')
  if (period.submit_progress_status && period.submit_progress_status !== 'Completed')
    return statusLabels[statusKeys.SUBMISSION];
  return null;
};

export const getStatus = (period, reportType) => {
  if (period.fund_validation_progress_status === 'Not started' && !period.fund_count)
    return statusKeys.AIF;
  if (
    period.jurisdiction_validation_progress_status === 'Not started' &&
    !period.jurisdiction_count
  )
    return statusKeys.JURISDICTION;

  // Annex IV Nil Return: nothing to upload/extract/transform/model once every included fund's
  // national code is Nil Return — skip straight to Report logic.
  if (!(reportType === REPORT_TYPES.ANNEX_IV && isNilReturnPeriod(period))) {
    if (period.file_mapping_progress_status === 'Not started' || !period.file_count)
      return statusKeys.FILES;

    // Extraction logic -- start
    if (period.extraction_progress_status === 'Not started') return statusKeys.EXTRACTION;
    if (period.extraction_stats?.failed_extractions_number > 0) return statusKeys.EXTRACTION;
    if (period?.extraction_stats?.in_progress_extractions_number > 0)
      return statusKeys.EXTRACTION_IN_PROGRESS;
    if (period?.extraction_stats?.successful_extractions_number === 0)
      return statusKeys.EXTRACTION_IN_PROGRESS;
    // Extraction logic -- end

    // Transformation logic -- start
    const allTransformations =
      period?.transformation_stats?.successful_transformations_number +
      period?.transformation_stats?.failed_transformations_number +
      period?.transformation_stats?.in_progress_transformation_number;

    const isTransformationStartedOrCompleted =
      period.transformation_progress_status !== 'Not started';
    const skipTransformationStatusCheck =
      (allTransformations === 0 && isTransformationStartedOrCompleted) ||
      (isFromScratch(period) && isTransformationStartedOrCompleted);

    if (period.transformation_progress_status === 'Not started' && !skipTransformationStatusCheck)
      return statusKeys.TRANSFORMATION;

    if (
      period.transformation_stats?.failed_transformations_number > 0 &&
      !skipTransformationStatusCheck
    )
      return statusKeys.TRANSFORMATION;

    if (
      (period?.transformation_stats?.successful_transformations_number === 0 ||
        period?.transformation_stats?.in_progress_transformation_number > 0) &&
      !skipTransformationStatusCheck
    )
      return statusKeys.TRANSFORMATION;
    // Transformation logic -- end
  }

  // Report logic - start
  if (
    period.report_progress_status === 'Not started' ||
    (period?.report_task && period?.report_task?.status !== 'Completed')
  )
    return statusKeys.REPORT;

  if (period?.comments?.total_threads !== period?.comments?.resolved_threads)
    return statusKeys.REPORT;
  // Report logic - end

  // Stop advancing past APPROVAL / SUBMISSION for ANY non-Completed value
  // (was `=== 'Not started'` — which let 'In progress' fall through to END
  // and painted every stepper stage green while approval was still running).
  // Truthiness guard keeps pre-approval periods (undefined field) at END.
  if (period.approve_progress_status && period.approve_progress_status !== 'Completed')
    return statusKeys.APPROVAL;

  if (period.submit_progress_status && period.submit_progress_status !== 'Completed')
    return statusKeys.SUBMISSION;

  return statusKeys.END;
};

export const getPlaceholder = (period, reportType) => {
  if (period.fund_validation_progress_status === 'Not started' && !period.fund_count)
    return statusLabels[statusKeys.AIF];
  if (
    period.jurisdiction_validation_progress_status === 'Not started' &&
    !period.jurisdiction_count
  )
    return statusLabels[statusKeys.JURISDICTION];

  // Annex IV Nil Return: same shortcut as getStatus above.
  if (!(reportType === REPORT_TYPES.ANNEX_IV && isNilReturnPeriod(period))) {
    if (period.file_mapping_progress_status === 'Not started' || !period.file_count)
      return statusLabels[statusKeys.FILES];

    // Extraction logic -- start
    if (period.extraction_progress_status === 'Not started')
      return statusLabels[statusKeys.EXTRACTION];
    if (period.extraction_stats?.failed_extractions_number > 0) return 'Resolve issues first';
    if (period?.extraction_stats?.in_progress_extractions_number > 0)
      return statusLabels[statusKeys.EXTRACTION_IN_PROGRESS];
    if (period?.extraction_stats?.successful_extractions_number === 0)
      return statusLabels[statusKeys.EXTRACTION_IN_PROGRESS];
    // Extraction logic -- end

    // Transformation logic -- start
    const allTransformations =
      period?.transformation_stats?.successful_transformations_number +
      period?.transformation_stats?.failed_transformations_number +
      period?.transformation_stats?.in_progress_transformation_number;

    const isTransformationStartedOrCompleted =
      period.transformation_progress_status !== 'Not started';

    // Scratch periods: any non-'Not started' state advances past transformation regardless of
    // stats — mirrors getStatus above (see rationale there).
    const skipTransformationStatusCheck =
      (allTransformations === 0 && isTransformationStartedOrCompleted) ||
      (isFromScratch(period) && isTransformationStartedOrCompleted);

    if (
      period.transformation_stats?.failed_transformations_number > 0 &&
      !skipTransformationStatusCheck
    )
      return 'Resolve issues first';
    if (
      (!period.transformation_progress_status ||
        period.transformation_progress_status === 'Not started') &&
      !skipTransformationStatusCheck
    )
      return statusLabels[statusKeys.TRANSFORMATION];
    if (
      period?.transformation_stats?.successful_transformations_number === 0 &&
      !skipTransformationStatusCheck
    )
      return statusLabels[statusKeys.TRANSFORMATION];
    if (
      period?.transformation_stats?.in_progress_transformation_number > 0 &&
      !skipTransformationStatusCheck
    )
      return statusLabels[statusKeys.TRANSFORMATION_IN_PROGRESS];
    // Transformation logic -- end
  }

  if (
    period.report_progress_status === 'Not started' ||
    (period?.report_task && period?.report_task?.status !== 'Completed')
  )
    return statusLabels[statusKeys.REPORT];

  if (period?.comments?.total_threads !== period?.comments?.resolved_threads)
    return 'Resolve comments first';

  if (period.approve_progress_status && period.approve_progress_status !== 'Completed')
    return statusLabels[statusKeys.APPROVAL];
  // if (period.report_progress_status === 'Not started')
  if (period.submit_progress_status && period.submit_progress_status !== 'Completed')
    return statusLabels[statusKeys.SUBMISSION];
  return null;
};

// Guards against upstream code producing the literal strings "null" or
// "undefined" (e.g. template-literal concatenation over a null API field, or
// a backend echoing string "null"). React already swallows `null` /
// `undefined` values, but the stringified forms render as visible words.
const scrubLabel = value => {
  if (value == null) return null;
  if (value === 'null' || value === 'undefined' || value === 'NaN') return null;
  return value;
};

export const ActionLabel = ({
  label,
  color,
  onClick = () => {},
  leftIcon,
  rightIcon,
  placeholder,
  noFill = false,
  textDecoration = 'underline',
  rightSection,
  ...props
}) => {
  const safeLabel = scrubLabel(label);
  const safePlaceholder = scrubLabel(placeholder);
  return (
    <LabelContainer color={color} onClick={onClick} noFill={noFill} {...props}>
      {safeLabel ? (
        <div className="action-label-content">
          {leftIcon}
          <Label color={color} textDecoration={textDecoration}>
            {safeLabel}
          </Label>
          {rightIcon}
        </div>
      ) : (
        <Placeholder>{safePlaceholder}</Placeholder>
      )}

      {rightSection}
    </LabelContainer>
  );
};

// Disabled Source Files / Extraction / Transformation cell shown for Nil-Return
// periods. `message` is the column-specific reason (e.g. "no extraction required").
// `id` gives automation selectors a stable per-period, per-stage target.
export const NilReturnCell = ({ id, message = 'nothing is required' }) => (
  <Tooltip multiline w={220} label={`This period is a Nil Return — ${message}.`}>
    <span
      id={`${id}-tooltip-target`}
      style={{ display: 'inline-block', opacity: 0.6, cursor: 'not-allowed' }}
      tabIndex={0}
      aria-disabled="true"
      aria-label={`This period is a Nil Return — ${message}.`}
    >
      <ActionLabel
        id={id}
        style={{ width: 150, cursor: 'inherit' }}
        placeholder="Nil Return"
        textDecoration="none"
        type="button"
        disabled
        aria-disabled="true"
      />
    </span>
  </Tooltip>
);

export const CALCULATED_CELL_WIDTH = `calc(calc(100vw - 260px) / 8)`;
